import React, { useState, useMemo } from "react";
import { Plus, X, Edit, RefreshCcw, Trash2 } from "lucide-react";
import AddTasks from "../tasks/AddTasks";
import { getPriorityColor, getStatusColor } from "../../utils/colors";
import { FiCheckSquare } from "react-icons/fi";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import taskApi from "../../api/taskApi";
import { confirmSubmit } from "../../utils/confirmSubmit";
import SearchBar from "../../components/SearchBar";

const TasksTab = ({
  selectedClientData,
  onRefreshAccounts,
  navigate,
}) => {
  const token = useSelector((state) => state.auth.access_token);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [viewTask, setViewTask] = useState(null);
  const [editTaskId, setEditTaskId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [comments, setComments] = useState([{ content: "" }]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    dueDate: new Date().toISOString().split("T")[0],
    startDate: new Date().toISOString().split("T")[0],
    priority: "LOW",
    status: "TODO",
    assignedTo: "",
    account: "",
  });

  const [filters, setFilters] = useState({
    assignee: "all",
    priority: "all",
    status: "all",
  });

  // ... existing states
  const [searchResultTasks, setSearchResultTasks] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearchApi = async (query) => {
    // If query is too short, go back to showing original client tasks
    if (!query || query.trim().length < 3) {
      setSearchResultTasks(null);
      return;
    }

    setIsSearching(true);
    try {
      // Calling your taskApi.searchTask
      const res = await taskApi.searchTask(query, token);
      setSearchResultTasks(res.data || []);
    } catch (err) {
      console.error("Search error:", err);
      setSearchResultTasks([]); // Show empty if error
    } finally {
      setIsSearching(false);
    }
  };

  // const filteredTasks = useMemo(() => {
  //   if (!selectedClientData?.tasks) return [];

  //   let tasks = [...selectedClientData.tasks];
  //   // Filter
  //   if (filters.assignee !== "all") {
  //     tasks = tasks.filter(
  //       (task) => task?.assignedToName === filters.assignee
  //     );
  //   }
  //   if (filters.priority !== "all") {
  //     tasks = tasks.filter((task) => task.priority === filters.priority);
  //   }
  //   if (filters.status !== "all") {
  //     tasks = tasks.filter((task) => task.status === filters.status);
  //   }

  //   tasks.sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));

  //   return tasks;
  // }, [selectedClientData, filters]);


  const filteredTasks = useMemo(() => {
    // Logic: Search Results take priority over original tasks
    const baseTasks = searchResultTasks !== null
      ? searchResultTasks
      : (selectedClientData?.tasks || []);

    let tasks = [...baseTasks];

    // Apply your existing dropdown filters
    if (filters.assignee !== "all") {
      tasks = tasks.filter((task) => task?.assignedToName === filters.assignee);
    }
    if (filters.priority !== "all") {
      tasks = tasks.filter((task) => task.priority === filters.priority);
    }
    if (filters.status !== "all") {
      tasks = tasks.filter((task) => task.status === filters.status);
    }

    tasks.sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));
    return tasks;
  }, [selectedClientData, filters, searchResultTasks]); // Added searchResultTasks here
  const assignees = [
    ...new Set(
      selectedClientData?.tasks
        ?.map((t) => t?.assignedToName)
        .filter(Boolean)
    ),
  ];
  const priorities = [
    ...new Set(
      selectedClientData?.tasks?.map((t) => t?.priority).filter(Boolean)
    ),
  ];
  const statuses = [
    ...new Set(
      selectedClientData?.tasks?.map((t) => t?.status).filter(Boolean)
    ),
  ];

  const formatText = (text = "") =>
    text
      .toLowerCase()
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const handleDeleteTask = async (taskId) => {
    const isConfirmed = await confirmSubmit({
      title: "Confirm task delete",
      text: "Are you sure you want to delete this task?",
      confirmText: "Submit",
    });
    if (!isConfirmed) return;
    try {
      await taskApi.deleteTask(taskId, token);
      onRefreshAccounts();
      toast.success("Task deleted successfully");
    } catch (err) {
      toast.error("Failed to delete task");
      console.error("Failed to delete task", err);
    }

  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc", // asc | desc
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const getValueByPath = (obj, path) =>
    path.split(".").reduce((acc, key) => acc?.[key], obj);


  const sortedTasks = useMemo(() => {
    if (!sortConfig.key) return filteredTasks;

    return [...filteredTasks].sort((a, b) => {
      const aVal = getValueByPath(a, sortConfig.key);
      const bVal = getValueByPath(b, sortConfig.key);

      if (!aVal) return 1;
      if (!bVal) return -1;

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === "asc"
        ? aVal > bVal ? 1 : -1
        : aVal < bVal ? 1 : -1;
    });
  }, [filteredTasks, sortConfig]);

  const SortIcon = ({ column }) => {
    const isActive = sortConfig.key === column;

    return (
      <span className="inline-flex flex-col ml-1 text-[9px] leading-none">
        <span
          className={
            isActive && sortConfig.direction === "asc"
              ? "text-gray-900"
              : "text-gray-300"
          }
        >
          ▲
        </span>
        <span
          className={
            isActive && sortConfig.direction === "desc"
              ? "text-gray-900"
              : "text-gray-300"
          }
        >
          ▼
        </span>
      </span>
    );
  };


  return (
    <div className="space-y-3">
      {(!showCreateTask && !editTaskId) &&
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-2 py-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-lg font-bold text-slate-700">Client Tasks</h3>
          <button
            // onClick={() => navigate('/tasks/createTasks')}
            onClick={() => setShowCreateTask(true)}
            className="bg-primary text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            <span>Add Task</span>
          </button>
        </div>
        {selectedClientData?.tasks?.length !== 0 ?
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mt-1">
            <select
              className="border rounded-lg px-3 py-1 text-sm"
              value={filters.assignee}
              onChange={(e) =>
                setFilters({ ...filters, assignee: e.target.value })
              }
            >
              <option value="all">All Assignees</option>
              {assignees.map((name, idx) => (
                <option key={idx} value={name}>
                  {formatText(name)}
                </option>
              ))}
            </select>

            <select
              className="border rounded-lg px-3 py-1 text-sm"
              value={filters.priority}
              onChange={(e) =>
                setFilters({ ...filters, priority: e.target.value })
              }
            >
              <option value="all">All Priorities</option>
              {priorities.map((p, idx) => (
                <option key={idx} value={p}>
                  {formatText(p)}
                </option>
              ))}
            </select>

            <select
              className="border rounded-lg px-3 py-1 text-sm"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="all">All Status</option>
              {statuses.map((s, idx) => (
                <option key={idx} value={s}>
                  {formatText(s)}
                </option>
              ))}
            </select>

            <div className="relative">
              <SearchBar
                onQueryChange={handleSearchApi}
                placeholder="Search Tasks..."
              />
            </div>
            <div className="flex items-end justify-end">
              <button
                type="button"
                onClick={() => setFilters({
                  assignee: "all",
                  priority: "all",
                  status: "all",
                })}
                className="bg-primary py-1 px-2 rounded-md text-sm font-medium text-white hover:bg-primary [&:active_span]:animate-spin-once"
              >
                <span className="inline-block">
                  <RefreshCcw size={18} />
                </span>
              </button>
            </div>
          </div> : <></>}
        <div className="py-2 mt-1">
          {filteredTasks?.length !== 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left text-slate-700">#</th>
                    <th className="px-2 py-1 text-left text-slate-700 cursor-pointer" onClick={() => handleSort("title")}><span className="flex flex-row items-center gap-1">Task <SortIcon column="title" /></span></th>
                    <th className="px-2 py-1 text-left text-slate-700 cursor-pointer" onClick={() => handleSort("assignedToId")}><span className="flex flex-row items-center gap-1">Assignee <SortIcon column="assignedToId" /></span></th>
                    <th className="px-2 py-1 text-left text-slate-700 cursor-pointer" onClick={() => handleSort("priority")}><span className="flex flex-row items-center gap-1">Priority <SortIcon column="priority" /></span></th>
                    <th className="px-2 py-1 text-left text-slate-700 cursor-pointer" onClick={() => handleSort("status")}><span className="flex flex-row items-center gap-1">Status <SortIcon column="status" /></span></th>
                    <th className="px-2 py-1 text-left text-slate-700 cursor-pointer" onClick={() => handleSort("dueDate")}><span className="flex flex-row items-center gap-1">Due Date <SortIcon column="dueDate" /></span></th>
                    <th className="px-2 py-1 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedTasks.map((task, index) => (
                    <tr key={task?.id} className="hover:bg-gray-50">
                      <td className="px-2 py-1">{index + 1}</td>
                      <td className="px-2 py-1 font-medium">{task.title}</td>
                      <td className="px-2 py-1">{task?.assignedToName}</td>
                      <td className="px-2 py-1">
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${getPriorityColor(
                            task.priority
                          )}`}
                        >
                          {formatText(task.priority)}
                        </span>
                      </td>
                      <td className="px-2 py-1">
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(
                            task.status
                          )}`}
                        >
                          {formatText(task.status)}
                        </span>
                      </td>
                      <td className="px-2 py-1">{task.dueDate}</td>
                      <td className="px-2 py-1 text-center space-x-2">
                        <button
                          onClick={() => {
                            setEditTaskId(task?.id);
                            setFormData({
                              title: task.title || "",
                              description: task.description || "",
                              startDate: task.startDate || "",
                              dueDate: task.dueDate || "",
                              priority: task.priority || "",
                              status: task.status || "",
                              workflowId: task.workflowId || "",
                              assignedTo: task?.assignedToId || "",
                            });
                            setAttachments(task?.attachments);
                            setComments(task?.comments.length !== 0 ? task?.comments : [{ content: "" }]);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTask(task?.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
              <FiCheckSquare className="h-12 w-12 text-gray-400 mx-auto mb-1" />
              <h4 className="text-lg font-medium text-gray-900 mb-1">No Tasks</h4>
              <p className="text-gray-600 mb-4">
                No tasks have been assigned for this client yet.
              </p>
              {/* <button
                onClick={() => setShowCreateTask(true)}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Create First Task
              </button> */}
            </div>
          )}
        </div>
      </div>}


      {showCreateTask &&
          <AddTasks
            accontdId={selectedClientData.id}
            onRefreshAccounts={onRefreshAccounts}
            onClose={() => setShowCreateTask(false)}
            setShowCreateTask={setShowCreateTask}
            attachments={attachments}
            setAttachments={setAttachments}
            comments={comments}
            setComments={setComments}
            formData={formData}
            setFormData={setFormData}
          />}

      {editTaskId &&
          <AddTasks
            accontdId={selectedClientData?.id}
            taskId={editTaskId}
            isModal={true}
            onClose={() => setEditTaskId(null)}
            onRefreshAccounts={onRefreshAccounts}
            setShowCreateTask={setEditTaskId}
            attachments={attachments}
            setAttachments={setAttachments}
            comments={comments}
            setComments={setComments}
            formData={formData}
            setFormData={setFormData}
          />}

      {/* <Modal
        isOpen={editTaskId}
        title="Updated Tasks"
        onClose={() => setEditTaskId(false)}
      >
        <AddTasks
          accontdId={selectedClientData?.id}
          taskId={editTaskId}
          isModal={true}
          onClose={() => setEditTaskId(null)}
          onRefreshAccounts={onRefreshAccounts}
        />
      </Modal> */}

      {viewTask && (
        <div onClick={() => setViewTask(null)} className="fixed inset-0 -top-6 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-2xl max-w-2xl w-full">
            <div className="flex justify-between items-center px-2 py-1 border-b">
              <div className="text-lg font-semibold text-gray-900">
                Task Details
              </div>
              <button
                onClick={() => setViewTask(null)}
                className="p-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="relative p-3 text-sm bg-white rounded-xl shadow-md">
              <div className="mb-2">
                <div className="text-lg font-semibold text-gray-900 leading-tight">
                  {viewTask.title}
                </div>
                <p className="text-xs text-gray-400 mt-0">
                  Task Overview
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xs text-gray-400 mb-0">
                    {viewTask?.assignedToName ? "Assignee" : "Workflow"}
                  </p>
                  <p className="font-medium text-gray-800">
                    {viewTask?.assignedToName
                      || formatText(viewTask?.workflowName)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xs text-gray-400 mb-0">Due Date</p>
                  <p className="font-medium text-gray-800">
                    {viewTask.dueDate || "N/A"}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xs text-gray-400 mb-0">Priority</p>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                    ${viewTask.priority === "HIGH"
                      ? "bg-red-100 text-red-600"
                      : viewTask.priority === "MEDIUM"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-green-100 text-green-600"}
                  `}>
                    {formatText(viewTask.priority)}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-xs text-gray-400 mb-0">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                    ${viewTask.status === "Completed"
                      ? "bg-green-100 text-green-600"
                      : viewTask.status === "In Progress"
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-100 text-gray-600"}
                  `}>
                    {formatText(viewTask.status)}
                  </span>
                </div>
              </div>
              {viewTask.description && (
                <div className="mt-2 border-t pt-2">
                  <p className="text-xs text-gray-400 mb-2">Description</p>
                  <div className="bg-gray-50 rounded-lg px-3 py-2 text-gray-700 leading-relaxed">
                    {viewTask.description}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksTab;
